/**
 * Email Service using n8n Webhook
 * Handles sending invitation emails and other notifications via n8n
 */

/**
 * Email service configuration using environment variables (Vite/React)
 */
const EMAIL_CONFIG = {
  webhookUrl: import.meta.env.VITE_N8N_WEBHOOK_URL,
  baseUrl: import.meta.env.VITE_BASE_URL || window.location.origin,
};

/**
 * Send an invitation email to a user using n8n webhook
 * @param {string} email - Recipient email address
 * @param {string} inviteToken - Unique invite token
 * @param {Object} inviteData - Invite data including pre-filled info
 * @param {string} inviterName - Name of the person sending the invite
 * @returns {Promise<Object>} Result of email send operation
 */
export const sendInviteEmail = async (email, inviteToken, inviteData = {}, inviterName = 'Your administrator') => {
  try {
    // Validate configuration
    if (!EMAIL_CONFIG.webhookUrl) {
      console.warn('Email service not configured. Missing VITE_N8N_WEBHOOK_URL environment variable');
      return sendSimulatedEmail({ to: email, inviteToken, inviterName });
    }

    // Generate the invite URL with proper URL encoding to handle base64 special characters (+, /, =)
    const encodedInviteToken = encodeURIComponent(inviteToken);
    const inviteUrl = `${EMAIL_CONFIG.baseUrl}/register?invite=${encodedInviteToken}`;
    
    // DEBUG: Log URL generation for diagnosis
    console.log('🔍 DEBUGGING: Email URL generation:', {
      originalToken: inviteToken,
      encodedToken: encodedInviteToken,
      hasSpecialChars: {
        plus: inviteToken.includes('+'),
        slash: inviteToken.includes('/'),
        equals: inviteToken.includes('=')
      },
      finalUrl: inviteUrl,
      timestamp: new Date().toISOString()
    });
    
    // Extract role information for personalized message
    const role = inviteData.pre_filled_data?.role;
    const roleText = role ? ` as a ${getRoleDisplayName(role)}` : '';
    
    // Prepare email data for n8n webhook
    const emailPayload = {
      to: email,
      subject: `You've been invited to AI Coach Assist`,
      html: generateInviteEmailHTML(email, inviteUrl, inviterName, roleText, inviteData)
    };

    // Send email via n8n webhook
    const response = await fetch(EMAIL_CONFIG.webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailPayload),
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`n8n webhook error: ${response.status} ${response.statusText} - ${errorData}`);
    }

    const result = await response.json();
    
    console.log('✅ Invitation email sent successfully via n8n:', {
      to: email,
      webhookResponse: result
    });

    return { 
      success: true, 
      messageId: result.messageId || `n8n_${Date.now()}`,
      message: result.message || 'Email sent successfully',
      provider: 'n8n'
    };

  } catch (error) {
    console.error('❌ Email sending failed:', error);
    return { 
      success: false, 
      error: error.message,
      details: error
    };
  }
};

/**
 * Simulate email sending when configuration is incomplete
 * @param {Object} emailData - Email data for simulation
 * @returns {Promise<Object>} Simulated result
 */
const sendSimulatedEmail = async (emailData) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  console.log('📧 SIMULATED EMAIL (Configure n8n webhook for production):', {
    to: emailData.to,
    inviteToken: emailData.inviteToken,
    inviterName: emailData.inviterName,
    note: 'Add VITE_N8N_WEBHOOK_URL to .env file'
  });
  
  return { 
    success: true, 
    messageId: `sim_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    provider: 'simulation',
    note: 'Configure n8n webhook URL for production email sending.'
  };
};

/**
 * Generate HTML email content for invitation
 * @param {string} email - Recipient email
 * @param {string} inviteUrl - Invite URL
 * @param {string} inviterName - Inviter name
 * @param {string} roleText - Role description
 * @param {Object} inviteData - Additional invite data
 * @returns {string} HTML email content
 */
const generateInviteEmailHTML = (email, inviteUrl, inviterName, roleText, inviteData) => {
  const expiresAt = inviteData.expires_at ? new Date(inviteData.expires_at).toLocaleDateString() : 'soon';
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>You're Invited to AI Coach Assist</title>
    </head>
    <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f7fafc;">
      <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; border-radius: 10px; text-align: center; margin-bottom: 30px;">
        <h1 style="color: white; margin: 0; font-size: 28px; font-weight: 600;">
          Life Coach Assist
        </h1>
        <p style="color: #e2e8f0; margin: 10px 0 0 0; font-size: 16px;">
          Patent Pending - Your Always-On Connection to Your Coach
        </p>
      </div>
      
      <div style="background: white; padding: 30px; border-radius: 10px; margin-bottom: 30px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);">
        <h2 style="color: #2d3748; margin-top: 0; font-size: 24px;">
          You've Been Invited!
        </h2>
        <p style="font-size: 16px; margin-bottom: 20px;">
          Hi there! 👋
        </p>
        <p style="font-size: 16px; margin-bottom: 20px;">
          <strong>${inviterName}</strong> has invited you to join AI Coach Assist${roleText}. 
        </p>
        <p style="font-size: 16px; margin-bottom: 25px; color: #4a5568;">
          Our platform provides 24/7 support for your growth journey:
        </p>
        <ul style="color: #4a5568; padding-left: 20px; margin-bottom: 25px;">
          <li style="margin-bottom: 8px;">📊 Captures your celebrations, challenges, regrets, and action plans</li>
          <li style="margin-bottom: 8px;">🤖 Provides AI-powered guidance when your coach is unavailable</li>
          <li style="margin-bottom: 8px;">📞 Available 24/7 to support your growth journey</li>
        </ul>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="${inviteUrl}" 
             style="background: #667eea; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px; display: inline-block; box-shadow: 0 4px 14px 0 rgba(102, 126, 234, 0.39);">
            Accept Invitation & Create Account
          </a>
        </div>
        
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #667eea;">
          <p style="margin: 0; font-size: 14px; color: #4a5568;">
            <strong>📧 Email:</strong> ${email}<br>
            <strong>⏰ Expires:</strong> ${expiresAt}<br>
            <strong>👤 Invited by:</strong> ${inviterName}
          </p>
        </div>
      </div>
      
      <div style="background: white; padding: 20px; border-radius: 10px; margin-bottom: 20px;">
        <h3 style="color: #2d3748; font-size: 18px; margin-top: 0;">Having trouble?</h3>
        <p style="font-size: 14px; color: #718096; margin-bottom: 15px;">
          If the button above doesn't work, copy and paste this link into your browser:
        </p>
        <p style="font-size: 12px; background: #f1f5f9; padding: 10px; border-radius: 4px; word-break: break-all; color: #667eea;">
          ${inviteUrl}
        </p>
      </div>
      
      <div style="font-size: 14px; color: #718096; text-align: center;">
        <p>
          This invitation was sent by ${inviterName}. If you didn't expect this invitation, 
          you can safely ignore this email.
        </p>
        <p style="margin-top: 20px; font-size: 12px; border-top: 1px solid #e2e8f0; padding-top: 20px;">
          © ${new Date().getFullYear()} Life Coach Assist. All rights reserved.
        </p>
      </div>
    </body>
    </html>
  `;
};

/**
 * Get display name for user roles
 * @param {string} role - User role
 * @returns {string} Display name
 */
const getRoleDisplayName = (role) => {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Administrator';
    case 'COACH':
      return 'Coach';
    case 'CLIENT':
      return 'Client';
    default:
      return role;
  }
};

/**
 * Send a password reset notification email using n8n webhook
 * @param {string} email - Recipient email
 * @param {string} resetUrl - Password reset URL
 * @returns {Promise<Object>} Result of email send operation
 */
export const sendPasswordResetEmail = async (email, resetUrl) => {
  try {
    if (!EMAIL_CONFIG.webhookUrl) {
      return sendSimulatedEmail({ to: email, type: 'password_reset' });
    }

    // Prepare email data for n8n webhook
    const emailPayload = {
      to: email,
      subject: 'Reset your AI Coach Assist password',
      html: `
        <html>
        <body style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #667eea;">🎤 AI Coach Assist</h1>
          </div>
          <h2 style="color: #2d3748;">Password Reset Request</h2>
          <p>You requested to reset your password. Click the button below to set a new password:</p>
          <div style="text-align: center; margin: 30px 0;">
            <a href="${resetUrl}" 
               style="background: #667eea; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
              Reset My Password
            </a>
          </div>
          <p style="color: #718096; font-size: 14px;">
            If you didn't request this password reset, please ignore this email. 
            This link will expire in 24 hours for security.
          </p>
          <p style="color: #718096; font-size: 12px; border-top: 1px solid #e2e8f0; padding-top: 15px; margin-top: 30px;">
            If the button doesn't work, copy and paste this link: ${resetUrl}
          </p>
        </body>
        </html>
      `
    };

    const response = await fetch(EMAIL_CONFIG.webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailPayload),
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`n8n webhook error: ${response.status} - ${errorData}`);
    }

    const result = await response.json();
    return { 
      success: true, 
      messageId: result.messageId || `n8n_${Date.now()}`,
      provider: 'n8n'
    };

  } catch (error) {
    console.error('Password reset email failed:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Test email configuration
 * @returns {Promise<Object>} Configuration test result
 */
export const testEmailConfiguration = async () => {
  const config = {
    hasWebhookUrl: !!EMAIL_CONFIG.webhookUrl,
    webhookUrl: EMAIL_CONFIG.webhookUrl ? EMAIL_CONFIG.webhookUrl.substring(0, 50) + '...' : null,
    baseUrl: EMAIL_CONFIG.baseUrl
  };

  const isConfigured = config.hasWebhookUrl;

  return {
    isConfigured,
    config,
    message: isConfigured 
      ? 'Email service is properly configured with n8n webhook'
      : 'Email service requires configuration. Add VITE_N8N_WEBHOOK_URL environment variable'
  };
};